import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Define access types for different pages
export const PAGE_ACCESS_TYPES = {
  ADMIN_SPECIFIC_ONLY: 'admin_specific_only',     // Branding, Client
  AGENCY_ADMIN_ONLY: 'agency_admin_only',         // Teams, Orders, Proposals, Invoices, Billing
  CLIENT_USERS_ONLY: 'client_users_only',         // Profile
  RESTRICTED_USERS: 'restricted_users',           // Client User List
} as const;

export type PageAccessType = typeof PAGE_ACCESS_TYPES[keyof typeof PAGE_ACCESS_TYPES];

// Access check functions
const checkPageAccess = (user: any, accessType: PageAccessType, agencyName?: string): boolean => {
  if (!user || !user.user) {
    return false;
  }

  const userType = user.user.user_type;
  const permissionType = user.user.permission_type;
  const restrictedTypes = ["Accounting", "Read Only Admin", "Sales Support Coordinator", "Integrated-User"];

  switch (accessType) {
    case PAGE_ACCESS_TYPES.ADMIN_SPECIFIC_ONLY:
      // Branding & Client pages
      // Deny: restricted types OR (Client without admin permission)
      return !(restrictedTypes.includes(userType) || (userType === 'Client' && permissionType !== 'admin'));

    case PAGE_ACCESS_TYPES.AGENCY_ADMIN_ONLY:
      // Teams, Orders, Proposals, Invoices, Billing
      // Allow only: Client with admin permission
      return userType === 'Client' && permissionType === 'admin' && agencyName !== 'Q1Media';

    case PAGE_ACCESS_TYPES.CLIENT_USERS_ONLY:
      // Profile page
      // Allow only: Client users (regardless of permission)
      return userType === 'Client';

    case PAGE_ACCESS_TYPES.RESTRICTED_USERS:
      // Client User List
      // Deny: restricted types OR any Client user
      return !(restrictedTypes.includes(userType) || userType === 'Client');

    default:
      return false;
  }
};

// Hook for page access control
export const usePageAccess = (accessType: PageAccessType): void => {
  const navigate = useNavigate();
  const { user, userError } = useSelector((state: any) => state.login);
  const { agency } = useSelector((state: any) => state.agency);

  useEffect(() => {
    if (!user || userError) {
      navigate('/analytics');
      return;
    }

    const hasAccess = checkPageAccess(user, accessType, agency?.data?.agency_name);

    if (!hasAccess) {
      navigate('/analytics');
    }
  }, [user, userError, navigate, accessType, agency]);
};

// Utility function to check access without navigation
export const checkUserPageAccess = (user: any, accessType: PageAccessType, agencyName?: string): boolean => {
  return checkPageAccess(user, accessType, agencyName);
};

// Helper function to get current user and agency from Redux
export const useCurrentUser = () => {
  const { user, userError } = useSelector((state: any) => state.login);
  const { agency } = useSelector((state: any) => state.agency);
  return { user, userError, agency };
};
